/**
 * Live-stream concerns of a media page: active polling, autoplay retry and
 * the start/end placeholder. No-ops when there is no live workflow.
 *
 * Detecting *whether* content is currently in the live workflow's "active"
 * state is a separate concern (`useLiveWorkflowState` / `fetchLiveWorkflowActive`
 * below) because the two players resolve it differently: the media page block
 * already lives inside the Volto content/redux flow, while the embed player is
 * a standalone page that must carry its own auth (token + optional pathkey
 * query param) on every request. Passing the resolved boolean in keeps the
 * rest of the live-stream logic identical for both.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { getWorkflow } from '@plone/volto/actions/workflow/workflow';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { dateTimeStringAppendZ } from '@package/helpers/utils';
import { useLiveActivePoll } from 'volto-base-addon/components/manage/Blocks/VideoJS/LiveActivePollHook';
import { withPathKeyParam } from 'volto-base-addon/helpers/pathKey';

const messages = defineMessages({
  liveStartsAt: {
    id: 'liveStartsAt',
    defaultMessage: 'Live stream starts at',
  },
  liveStartsSoon: {
    id: 'liveStartsSoon',
    defaultMessage: 'Live stream will start soon',
  },
  liveEndedAt: {
    id: 'liveEndedAt',
    defaultMessage: 'Live stream ended at',
  },
  liveNotStarted: {
    id: 'liveNotStarted',
    defaultMessage: 'Live stream has not started yet',
  },
  videoUnavailable: {
    id: 'videoUnavailable',
    defaultMessage: 'Video is currently unavailable.',
  },
});

/**
 * Resolves the live-stream workflow's "active" state through the normal
 * Volto redux content flow. For the media page block, which is already
 * driven by the content store, this is a same-origin request with no need
 * for the embed's pathkey plumbing.
 */
export const useLiveWorkflowState = (content) => {
  const dispatch = useDispatch();

  const live_workflow_state = useSelector(
    (state) => state.workflow.chain?.live_stream_workflow?.state,
  );

  // Populate state.workflow so live_stream_workflow.state is available.
  useEffect(() => {
    if (content?.['@id']) {
      dispatch(getWorkflow(flattenToAppURL(content['@id'])));
    }
  }, [dispatch, content]);

  return live_workflow_state?.id === 'active';
};

/**
 * Resolves the live-stream workflow's "active" state with a direct API call
 * instead of redux, carrying its own auth. For contexts such as the embed
 * player that sit outside the Volto content store and must send the
 * pathkey (when one applies) as a request parameter rather than a cookie.
 */
export const fetchLiveWorkflowActive = async (
  path,
  { api, token, pathKey } = {},
) => {
  if (!path || !api) {
    return false;
  }
  try {
    const response = await api.get(
      withPathKeyParam(`${flattenToAppURL(path)}/@workflow`, pathKey),
      { headers: { Authorization: token ? `Bearer ${token}` : undefined } },
    );
    return response?.chain?.live_stream_workflow?.state?.id === 'active';
  } catch (error) {
    // A failed lookup must not break the rest of the page — it only gates
    // the live-specific UI, which then simply behaves as non-live.
    // eslint-disable-next-line no-console
    console.error('Error fetching live workflow state:', error);
    return false;
  }
};

export const useLiveStream = (content, isLiveWorkflowActive) => {
  const intl = useIntl();

  const [liveShouldPoll, setLiveShouldPoll] = useState(true);
  const [liveForceAutoplay, setLiveForceAutoplay] = useState(false);

  const isLiveStream = !!content && !!isLiveWorkflowActive;

  const liveActiveCallback = useCallback(() => {
    setLiveShouldPoll(false);
  }, []);

  const isLiveActive = useLiveActivePoll(
    content?.live_stream_url,
    isLiveStream && liveShouldPoll,
    10000,
    liveActiveCallback,
  );

  const applyLiveOptions = useCallback(
    (options) => {
      if (!isLiveStream || !options) {
        return;
      }
      options.playbackRates = null;
      // Autoplay once live goes active, if the user already tried to play.
      if (liveForceAutoplay && isLiveActive) {
        options.autoplay = true;
      }
    },
    [isLiveStream, liveForceAutoplay, isLiveActive],
  );

  const getPlaceholderMessage = useCallback(() => {
    if (!isLiveStream) {
      return intl.formatMessage(messages.videoUnavailable);
    }
    const start = content.live_stream_start
      ? new Date(dateTimeStringAppendZ(content.live_stream_start))
      : null;
    const end = content.live_stream_end
      ? new Date(dateTimeStringAppendZ(content.live_stream_end))
      : null;
    const now = new Date();

    if (end && now > end) {
      return (
        <>
          {intl.formatMessage(messages.liveEndedAt)}{' '}
          <FormattedDate date={end} includeTime></FormattedDate>
        </>
      );
    }
    if (start) {
      if (now < start) {
        return (
          <>
            {intl.formatMessage(messages.liveStartsAt)}{' '}
            <FormattedDate date={start} includeTime></FormattedDate>
          </>
        );
      }
      return intl.formatMessage(messages.liveStartsSoon);
    }
    return intl.formatMessage(messages.liveNotStarted);
  }, [isLiveStream, content, intl]);

  // On a live-not-active error, resume polling and arm autoplay for go-live.
  const handlePlayerError = useCallback(
    (error) => {
      if (error?.code === 4 && isLiveStream) {
        setLiveShouldPoll(true);
        setLiveForceAutoplay(true);
      }
    },
    [isLiveStream],
  );

  const handleXhrResponse = useCallback(
    (request, error, response) => {
      if ((error || response?.statusCode !== 200) && isLiveStream) {
        setLiveShouldPoll(true);
        setLiveForceAutoplay(true);
      }
    },
    [isLiveStream],
  );

  return {
    isLiveStream,
    isLiveActive,
    liveForceAutoplay,
    applyLiveOptions,
    getPlaceholderMessage,
    handlePlayerError,
    handleXhrResponse,
  };
};

export default useLiveStream;
